import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AlertDialogData, AlertDialogResult } from './alert-dialog-config';

/**
 * Custom notification / confirm / prompt dialog (eMeni look), modelled on the
 * Kiosk app's alert component. Replaces SweetAlert2: a MatDialog host means no
 * background scroll-jump and no z-index conflicts with the rest of the app.
 *
 * Driven entirely by {@link AlertDialogData}; usually opened through
 * `AlertService` rather than directly.
 */
@Component({
    templateUrl: './alert-dialog.component.html',
    styleUrls: ['./alert-dialog.component.scss'],
    standalone: false
})
export class AlertDialogComponent {
  /** Bound to the optional text input (prompt). */
  value = '';
  /** Bound to the optional checkbox ("don't ask again"). */
  checkboxValue = false;

  /** Trenutak otvaranja — Enter koji je otvorio dijalog ne sme odmah i da ga zatvori. */
  private readonly openedAt = performance.now();

  constructor(
    public dialogRef: MatDialogRef<AlertDialogComponent, AlertDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: AlertDialogData,
    private translate: TranslateService
  ) {
    this.value = data.inputValue ?? '';
    this.checkboxValue = data.checkboxChecked ?? false;

    // CDK šalje keydown samo NAJVIŠEM dijalogu, bez obzira na fokus (autoFocus je
    // isključen), pa Enter zatvara poruke jednu po jednu. Tok se završava sa dijalogom.
    dialogRef.keydownEvents().subscribe((event) => this.onKeydown(event));
  }

  /** Samo dugme "U redu" — bez otkaži/ne i bez polja za unos (ono ima svoj Enter). */
  private get isSingleButton(): boolean {
    return this.showConfirm && !this.showCancel && !this.showDeny && !this.data.input;
  }

  /**
   * Enter = "U redu" kad je to jedino dugme (kao nekad SweetAlert). Potvrde sa više dugmadi
   * se Enter-om NE potvrđuju, da slučajan Enter ne bi odobrio radnju.
   */
  private onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.repeat || event.isComposing || !this.isSingleButton) {
      return;
    }

    // Isti Enter je otvorio dijalog (sinhroni handler na polju ispod) — ne zatvaraj ga odmah.
    if (event.timeStamp < this.openedAt) {
      return;
    }

    // Bez nativnog klika na dugme u fokusu i bez keypress-a ka osluškivačima iza dijaloga
    // (npr. PIN prijava bi inače ponovo poslala zahtev).
    event.preventDefault();

    if (this.dialogRef.getState() === MatDialogState.OPEN) {
      this.confirm();
    }
  }

  get iconName(): string {
    switch (this.data.type) {
      case 'error': return 'error_outline';
      case 'warning': return 'warning_amber';
      case 'info': return 'info';
      case 'success': return 'check_circle';
      case 'question': return 'help_outline';
      default: return 'info';
    }
  }

  get showConfirm(): boolean { return this.data.showConfirmButton !== false; }
  get confirmText(): string { return this.data.confirmButtonText || this.label('alertDialog.ok', 'U redu'); }
  get showCancel(): boolean { return this.data.showCancelButton === true; }
  get cancelText(): string { return this.data.cancelButtonText || this.label('cancel', 'Otkaži'); }
  get showDeny(): boolean { return this.data.showDenyButton === true; }
  get denyText(): string { return this.data.denyButtonText || this.label('confirmNo', 'Ne'); }
  get closeLabel(): string { return this.label('alertDialog.close', 'Zatvori'); }

  /**
   * Default text in the current language, resolved at display time so a language switch
   * reaches an open dialog too. Until the translations are loaded (e.g. the offline alert
   * at startup) `instant` returns the key itself — the Serbian default is shown instead.
   */
  private label(key: string, fallback: string): string {
    const text = this.translate.instant(key);
    return text && text !== key ? text : fallback;
  }

  confirm(): void {
    this.dialogRef.close({ confirmed: true, value: this.value, checkbox: this.checkboxValue });
  }

  deny(): void {
    this.dialogRef.close({ confirmed: false, denied: true, checkbox: this.checkboxValue });
  }

  cancel(): void {
    this.dialogRef.close({ confirmed: false, checkbox: this.checkboxValue });
  }
}
